import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// Use service layer instead of direct Firestore access
import { getAllForms, getForm, deleteForm } from '../../services/formService';
import { getFacilityGroupNames, getFacilityNames } from '../../services/facilityService';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TextField,
  InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AssignmentIcon from '@mui/icons-material/Assignment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GridViewIcon from '@mui/icons-material/GridView';
import ListIcon from '@mui/icons-material/List';
import SearchIcon from '@mui/icons-material/Search';

import AssignFormModal from './AssignFormModal';
import { ReactFormGenerator } from 'react-form-builder2';
import { styled } from '@mui/material/styles';

const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
}));

const Forms = () => {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [formAssignments, setFormAssignments] = useState({});
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [orderDirection, setOrderDirection] = useState('asc');

  const fetchFormAssignments = async (formId) => {
    try {
      // Use the service layer instead of direct Firestore access
      const formData = await getForm(formId);
      
      // Get assignments from the form document
      const assignments = formData.assignments || {
        facilityGroups: [],
        facilities: [],
        updatedAt: null
      };

      // Get facility group names
      let groupNames = [];
      if (assignments.facilityGroups?.length > 0) {
        // Filter out undefined/null values
        const validGroupIds = assignments.facilityGroups.filter(id => id !== undefined && id !== null);
        if (validGroupIds.length > 0) {
          groupNames = await getFacilityGroupNames(validGroupIds);
        }
      }

      // Get facility names
      let facilityNames = [];
      if (assignments.facilities?.length > 0) {
        // Filter out undefined/null values
        const validFacilityIds = assignments.facilities.filter(id => id !== undefined && id !== null);
        if (validFacilityIds.length > 0) {
          facilityNames = await getFacilityNames(validFacilityIds);
        }
      }

      return {
        facilities: assignments.facilities?.length || 0,
        groups: assignments.facilityGroups?.length || 0,
        facilityNames,
        groupNames,
        updatedAt: assignments.updatedAt
      };
    } catch (err) {
      console.error(`Error fetching assignments for form ${formId}:`, err);
      return { 
        facilities: 0, 
        groups: 0,
        facilityNames: [],
        groupNames: [],
        updatedAt: null
      };
    }
  };

  const fetchForms = useCallback(async () => {
    try {
      // Use the service layer instead of direct Firestore access
      const formsResponse = await getAllForms();
      
      // Extract the forms array from the response
      const formsData = formsResponse.forms || [];
      
      // Ensure formsData is an array
      if (!Array.isArray(formsData)) {
        console.warn('Forms data is not an array:', formsData);
        setForms([]);
        return;
      }
      
      // Sort forms by creation date (newest first)
      const sortedForms = formsData.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt).getTime();
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt).getTime();
        return dateB - dateA;
      });

      // Fetch assignments for each form
      const assignments = {};
      await Promise.all(
        sortedForms.map(async (form) => {
          assignments[form.id] = await fetchFormAssignments(form.id);
        })
      );
      setFormAssignments(assignments);
      setForms(sortedForms);
    } catch (err) {
      console.error('Error fetching forms:', err);
      setError('Failed to load forms');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const handleCreateNew = () => {
    navigate('/admin/forms/new');
  };

  const handleEdit = (form) => {
    navigate(`/admin/forms/${form.id}/edit`);
  };

  const handleDelete = async (form) => {
    setFormToDelete(form);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!formToDelete) return;

    try {
      // Use the service layer instead of direct Firestore access
      await deleteForm(formToDelete.id);
      
      setForms(forms.filter(f => f.id !== formToDelete.id));
      setDeleteConfirmOpen(false);
      setFormToDelete(null);
    } catch (err) {
      console.error('Error deleting form:', err);
      setError('Failed to delete form');
    }
  };

  const handlePreview = (form) => {
    setSelectedForm(form);
    setPreviewOpen(true);
  };

  const handleAssign = (form) => {
    setSelectedForm(form);
    setAssignModalOpen(true);
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleSort = (property) => {
    const isAsc = sortField === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setSortField(property);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const sortForms = (forms) => {
    return [...forms].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'createdAt':
          aValue = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt).getTime();
          bValue = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt).getTime();
          break;
        case 'updatedAt':
          aValue = formAssignments[a.id]?.updatedAt?.toDate ? formAssignments[a.id].updatedAt.toDate().getTime() : 
                   (formAssignments[a.id]?.updatedAt ? new Date(formAssignments[a.id].updatedAt).getTime() : 0);
          bValue = formAssignments[b.id]?.updatedAt?.toDate ? formAssignments[b.id].updatedAt.toDate().getTime() : 
                   (formAssignments[b.id]?.updatedAt ? new Date(formAssignments[b.id].updatedAt).getTime() : 0);
          break;
        default:
          return 0;
      }

      if (orderDirection === 'desc') {
        [aValue, bValue] = [bValue, aValue];
      }

      if (aValue < bValue) return -1;
      if (aValue > bValue) return 1;
      return 0;
    });
  };

  const filterForms = (forms) => {
    if (!searchTerm) return forms;
    const searchLower = searchTerm.toLowerCase();
    return forms.filter(form => 
      form.name?.toLowerCase().includes(searchLower) ||
      form.description?.toLowerCase().includes(searchLower)
    );
  };

  const renderGridView = () => {
    const sortedAndFilteredForms = sortForms(filterForms(forms));
    
    return (
      <Grid container spacing={3}>
        {sortedAndFilteredForms.map((form) => (
          <Grid item xs={12} md={6} lg={4} key={form.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" component="h2">
                    {form.name}
                  </Typography>
                  <Box>
                    <Tooltip title="Preview">
                      <IconButton onClick={() => handlePreview(form)} size="small">
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleEdit(form)} size="small">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Assign">
                      <IconButton onClick={() => handleAssign(form)} size="small">
                        <AssignmentIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDelete(form)} size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Typography color="textSecondary" gutterBottom>
                  {form.description}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Created: {form.createdAt?.toDate ? form.createdAt.toDate().toLocaleDateString() : new Date(form.createdAt).toLocaleDateString()}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      label={`${formAssignments[form.id]?.facilities || 0} Facilities`}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <Chip 
                      label={`${formAssignments[form.id]?.groups || 0} Groups`}
                      size="small"
                    />
                  </Box>
                  {formAssignments[form.id]?.facilityNames?.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        Assigned to facilities:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {formAssignments[form.id].facilityNames.map((name, index) => (
                          <Chip
                            key={index}
                            label={name}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  {formAssignments[form.id]?.groupNames?.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        Assigned to groups:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {formAssignments[form.id].groupNames.map((name, index) => (
                          <Chip
                            key={index}
                            label={name}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  {formAssignments[form.id]?.updatedAt && (
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                      Last updated: {formAssignments[form.id].updatedAt.toDate ? 
                        formAssignments[form.id].updatedAt.toDate().toLocaleString() : 
                        new Date(formAssignments[form.id].updatedAt).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderListView = () => {
    const sortedAndFilteredForms = sortForms(filterForms(forms));
    
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'name'}
                  direction={sortField === 'name' ? orderDirection : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Assignments</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'createdAt'}
                  direction={sortField === 'createdAt' ? orderDirection : 'asc'}
                  onClick={() => handleSort('createdAt')}
                >
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'updatedAt'}
                  direction={sortField === 'updatedAt' ? orderDirection : 'asc'}
                  onClick={() => handleSort('updatedAt')}
                >
                  Last Updated
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedAndFilteredForms.map((form) => (
              <TableRow key={form.id}>
                <TableCell>{form.name}</TableCell>
                <TableCell>{form.description}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip 
                      label={`${formAssignments[form.id]?.facilities || 0} Facilities`}
                      size="small"
                    />
                    <Chip 
                      label={`${formAssignments[form.id]?.groups || 0} Groups`}
                      size="small"
                    />
                  </Box>
                </TableCell>
                <TableCell>{form.createdAt?.toDate ? form.createdAt.toDate().toLocaleDateString() : new Date(form.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  {formAssignments[form.id]?.updatedAt ? 
                    (formAssignments[form.id].updatedAt.toDate ? 
                      formAssignments[form.id].updatedAt.toDate().toLocaleString() : 
                      new Date(formAssignments[form.id].updatedAt).toLocaleString()) : 
                    'Never'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Preview">
                    <IconButton onClick={() => handlePreview(form)} size="small">
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton onClick={() => handleEdit(form)} size="small">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Assign">
                    <IconButton onClick={() => handleAssign(form)} size="small">
                      <AssignmentIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton onClick={() => handleDelete(form)} size="small" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return <Typography>Loading forms...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Forms
          </Typography>
        </CardContent>
      </HeaderCard>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="grid" aria-label="grid view">
              <GridViewIcon />
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <ListIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            placeholder="Search forms..."
            size="small"
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            Create New Form
          </Button>
        </Box>
      </Box>

      {viewMode === 'grid' ? renderGridView() : renderListView()}

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Preview Form: {selectedForm?.name}</DialogTitle>
        <DialogContent>
          {selectedForm && (
            <ReactFormGenerator
              form_action="/path/to/your/endpoint"
              form_method="POST"
              task_id={12}
              answer_data={{}}
              authenticity_token=""
              data={Array.isArray(selectedForm.formData) ? selectedForm.formData : []}
              variables={{}}
              read_only={false}
              hide_actions={false}
              display_short={false}
              action_name="Submit"
              onSubmit={() => {}}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Form Modal */}
      {selectedForm && (
        <AssignFormModal
          open={assignModalOpen}
          onClose={() => setAssignModalOpen(false)}
          form={selectedForm}
          onSave={fetchForms}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the form "{formToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Forms; 